const CONTACT_PATH = "/api/v1/contact";
const MAX_RESPONSE_BYTES = 4096;

export class ContactRequestError extends Error {
  constructor(kind) {
    super(kind);
    this.name = "ContactRequestError";
    this.kind = kind;
  }
}

function contactUrl(apiBaseUrl) {
  let base;
  try {
    base = new URL(String(apiBaseUrl ?? ""));
  } catch (_error) {
    throw new TypeError("Contact API base URL is invalid.");
  }
  if (
    !["http:", "https:"].includes(base.protocol)
    || base.username
    || base.password
    || base.pathname !== "/"
    || base.search
    || base.hash
  ) {
    throw new TypeError("Contact API base URL must be an HTTP origin.");
  }
  return new URL(CONTACT_PATH, base).href;
}

async function boundedJson(response) {
  const contentLength = Number(response.headers.get("content-length"));
  if (Number.isFinite(contentLength) && contentLength > MAX_RESPONSE_BYTES) {
    throw new ContactRequestError("unavailable");
  }
  const text = await response.text();
  if (new TextEncoder().encode(text).byteLength > MAX_RESPONSE_BYTES) {
    throw new ContactRequestError("unavailable");
  }
  try {
    return JSON.parse(text);
  } catch (_error) {
    throw new ContactRequestError("unavailable");
  }
}

function validContactDetails(value) {
  return value !== null
    && typeof value === "object"
    && !Array.isArray(value)
    && Object.keys(value).every((key) => ["name", "email", "discord"].includes(key))
    && (value.name === undefined || typeof value.name === "string")
    && (value.email === undefined || typeof value.email === "string")
    && (value.discord === undefined || typeof value.discord === "string");
}

function decodeContext(value) {
  if (
    value === null
    || typeof value !== "object"
    || Array.isArray(value)
    || value.schemaVersion !== 1
    || typeof value.authenticated !== "boolean"
    || !validContactDetails(value.prefill)
  ) {
    throw new ContactRequestError("unavailable");
  }
  return Object.freeze({
    authenticated: value.authenticated,
    prefill: Object.freeze({
      name: value.prefill.name ?? "",
      email: value.prefill.email ?? "",
      discord: value.prefill.discord ?? "",
    }),
  });
}

export function createContactRepository({ apiBaseUrl, fetchImpl = globalThis.fetch } = {}) {
  if (typeof fetchImpl !== "function") {
    throw new TypeError("Contact repository requires fetch.");
  }
  const url = contactUrl(apiBaseUrl);

  return Object.freeze({
    async loadContext({ signal } = {}) {
      let response;
      try {
        response = await fetchImpl(url, {
          method: "GET",
          credentials: "include",
          cache: "no-store",
          headers: { Accept: "application/json" },
          signal,
        });
      } catch (error) {
        if (error?.name === "AbortError") throw error;
        throw new ContactRequestError("unavailable");
      }
      if (!response.ok) throw new ContactRequestError("unavailable");
      return decodeContext(await boundedJson(response));
    },

    async submit(submission, { signal } = {}) {
      let response;
      try {
        response = await fetchImpl(url, {
          method: "POST",
          credentials: "include",
          cache: "no-store",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(submission),
          signal,
        });
      } catch (error) {
        if (error?.name === "AbortError") throw error;
        throw new ContactRequestError("unavailable");
      }
      if (response.status === 429) throw new ContactRequestError("rateLimited");
      if (response.status === 400) throw new ContactRequestError("invalid");
      if (!response.ok) throw new ContactRequestError("unavailable");
      const acknowledgement = await boundedJson(response);
      if (acknowledgement?.schemaVersion !== 1 || acknowledgement.accepted !== true) {
        throw new ContactRequestError("unavailable");
      }
    },
  });
}

function submissionFromForm(form) {
  const data = new FormData(form);
  const contact = Object.fromEntries(
    ["name", "email", "discord"]
      .map((key) => [key, String(data.get(key) ?? "").trim()])
      .filter(([, value]) => value),
  );
  return {
    schemaVersion: 1,
    subject: String(data.get("subject") ?? "").trim(),
    message: String(data.get("message") ?? "").trim(),
    contact,
  };
}

export function mountContactForm(form, {
  apiBaseUrl = globalThis.window?.__fishystuffApiBaseUrl,
  fetchImpl = globalThis.fetch,
} = {}) {
  const repository = createContactRepository({ apiBaseUrl, fetchImpl });
  const card = form.closest("[data-contact-card]");
  const success = card?.querySelector("[data-contact-success]");
  const successTitle = card?.querySelector("[data-contact-success-title]");
  const continueButton = card?.querySelector("[data-contact-continue]");
  const subject = form.elements.namedItem("subject");
  const message = form.elements.namedItem("message");
  const submit = form.querySelector("[data-contact-submit]");
  const submitLabel = form.querySelector("[data-contact-submit-label]");
  const submitIcon = form.querySelector("[data-contact-submit-icon]");
  const spinner = form.querySelector("[data-contact-spinner]");
  const status = form.querySelector("[data-contact-status]");
  const prefillNote = form.querySelector("[data-contact-prefill-note]");
  const contactDetails = Object.fromEntries(
    ["name", "email", "discord"].map((key) => [key, {
      input: form.elements.namedItem(key),
      panel: form.querySelector(`[data-contact-detail="${key}"]`),
      add: form.querySelector(`[data-contact-add="${key}"]`),
      remove: form.querySelector(`[data-contact-remove="${key}"]`),
    }]),
  );
  const copyEntries = [...form.querySelectorAll("[data-contact-copy-key]")]
    .map((element) => [element.dataset.contactCopyKey, element.textContent.trim()]);
  const copy = Object.fromEntries(copyEntries);
  const requiredCopy = [
    "submitLabel",
    "submittingLabel",
    "rateLimited",
    "unavailable",
    "invalid",
    "authenticatedPrefill",
  ];
  const detailElements = Object.values(contactDetails).flatMap(Object.values);
  if (
    ![card, success, successTitle, continueButton, subject, message, submit, submitLabel, submitIcon, spinner, status, prefillNote, ...detailElements].every(Boolean)
    || !requiredCopy.every((key) => copy[key])
  ) {
    throw new TypeError("Contact form markup is incomplete.");
  }

  const controller = new AbortController();
  const editedDetails = new Set();
  const setDetailActive = (key, active, focus = false) => {
    const detail = contactDetails[key];
    detail.panel.hidden = !active;
    detail.input.disabled = !active;
    detail.add.hidden = active;
    if (!active) detail.input.value = "";
    if (focus) (active ? detail.input : detail.add).focus();
  };
  for (const [key, detail] of Object.entries(contactDetails)) {
    setDetailActive(key, Boolean(detail.input.value));
    detail.input.addEventListener("input", () => editedDetails.add(key), { signal: controller.signal });
    detail.add.addEventListener("click", () => {
      editedDetails.add(key);
      setDetailActive(key, true, true);
    }, { signal: controller.signal });
    detail.remove.addEventListener("click", () => {
      editedDetails.add(key);
      setDetailActive(key, false, true);
    }, { signal: controller.signal });
  }

  const showStatus = (text, tone = "neutral") => {
    status.textContent = text;
    status.classList.remove("text-success", "text-error", "text-base-content/70");
    status.classList.add(tone === "success" ? "text-success" : tone === "error" ? "text-error" : "text-base-content/70");
  };

  repository.loadContext({ signal: controller.signal }).then((context) => {
    let addedPrefill = false;
    if (context.authenticated) {
      for (const [key, value] of Object.entries(context.prefill)) {
        if (value && !editedDetails.has(key)) {
          contactDetails[key].input.value = value;
          setDetailActive(key, true);
          addedPrefill = true;
        }
      }
    }
    prefillNote.hidden = !addedPrefill;
  }).catch((error) => {
    if (error?.name !== "AbortError") {
      showStatus(copy.unavailable, "error");
    }
  });
  const clearSubmittedMessage = () => {
    const contactState = Object.fromEntries(
      Object.entries(contactDetails).map(([key, detail]) => [key, {
        active: !detail.input.disabled,
        value: detail.input.value,
      }]),
    );
    form.reset();
    for (const [key, state] of Object.entries(contactState)) {
      contactDetails[key].input.value = state.value;
      setDetailActive(key, state.active);
    }
    showStatus("");
  };

  continueButton.addEventListener("click", () => {
    form.inert = false;
    form.removeAttribute("aria-hidden");
    success.hidden = true;
    subject.focus();
  }, { signal: controller.signal });

  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    if (!form.reportValidity()) {
      showStatus(copy.invalid, "error");
      return;
    }
    submit.disabled = true;
    submitIcon.hidden = true;
    spinner.hidden = false;
    submitLabel.textContent = copy.submittingLabel;
    showStatus("");
    try {
      await repository.submit(submissionFromForm(form), { signal: controller.signal });
      clearSubmittedMessage();
      success.hidden = false;
      successTitle.focus();
      form.inert = true;
      form.setAttribute("aria-hidden", "true");
    } catch (error) {
      if (error?.name === "AbortError") return;
      const key = error instanceof ContactRequestError ? error.kind : "unavailable";
      showStatus(key === "rateLimited" ? copy.rateLimited : key === "invalid" ? copy.invalid : copy.unavailable, "error");
    } finally {
      submit.disabled = false;
      submitIcon.hidden = false;
      spinner.hidden = true;
      submitLabel.textContent = copy.submitLabel;
    }
  }, { signal: controller.signal });

  return Object.freeze({ dispose: () => controller.abort() });
}

if (typeof document !== "undefined" && typeof document.querySelectorAll === "function") {
  for (const form of document.querySelectorAll("[data-contact-form]")) {
    mountContactForm(form);
  }
}
